<template>
  <div class="rounded shadow-xs w-full max-w-3xl mx-auto" @click.stop>
    <div class="rounded shadow-lg bg-white overflow-hidden">
      <div class="h-10 bg-white pl-4">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <h1 class="text-sm">
              {{ orderItem.name }}
            </h1>
          </div>
          <div class="flex-none">
            <button
              class="h-10 w-10 inline-flex items-center justify-center hover:bg-gray-200 focus:outline-none"
              @click="$emit('close')"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" class="inline-flex w-5 h-5">
                <path
                  d="M12 10.586l4.95-4.95 1.414 1.414-4.95
                  4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95
                  4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05
                  5.636z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="bg-blue-900 text-white p-6 space-y-4">
        <ul class="text-sm flex space-x-12">
          <li>
            <h3 class="text-blue-400 text-sm">Status</h3>
            <span class="text-green-400">In progress</span>
          </li>
        </ul>
      </div>
      <div class="bg-blue-900">
        <nav class="text-white text-sm px-2">
          <button
            class="px-4 py-2 inline-flex focus:outline-none rounded-t"
            :class="[currentTab === 'specs' ? 'bg-gray-100 text-gray-900' : 'text-blue-200']"
            @click="currentTab = 'specs'"
          >
            Specs
          </button>
          <button
            class="px-4 py-2 inline-flex focus:outline-none rounded-t"
            :class="[currentTab === 'attachments' ? 'bg-gray-100 text-gray-900' : 'text-blue-200']"
            @click="currentTab = 'attachments'"
          >
            Attachments
          </button>
          <button
            class="px-4 py-2 inline-flex focus:outline-none rounded-t"
            :class="[currentTab === 'examples' ? 'bg-gray-100 text-gray-900' : 'text-blue-200']"
            @click="currentTab = 'examples'"
          >
            Examples
          </button>
          <button
            class="px-4 py-2 inline-flex focus:outline-none rounded-t"
            :class="[currentTab === 'reviewers' ? 'bg-gray-100 text-gray-900' : 'text-blue-200']"
            @click="currentTab = 'reviewers'"
          >
            Reviewers
          </button>
        </nav>
      </div>
      <div class="p-12 bg-gray-100">
        <template v-if="currentTab === 'specs'">
          <div class="space-y-4">
            <VTextEditor :content="data.requirements" @change="updateRequirements" />
          </div>
        </template>
        <template v-else-if="currentTab === 'attachments'">
          <AttachmentsTab
            :account-id="accountId"
            :client-id="clientId"
            :order-item-id="orderItemId"
            :links="data.links"
            :notes="data.notes"
            :media="data.media"
            @changed="prepareData(orderItem)"
          />
        </template>
        <template v-else-if="currentTab === 'reviewers'">
          <BaseFormSelect
            v-model="data.review"
            :options="reviewOptions"
            required
            @input="updateReview"
          />
        </template>
        <template v-else>
          <ExamplesManager :order-item-id="orderItemId" />
        </template>

        <portal to="modal-aside">
          <Product
            v-if="currentTab === 'specs' && product && product.requirements"
            :product="product"
          />
        </portal>
      </div>
    </div>
  </div>
</template>

<script>
import VTextEditor from '@/components/Common/VTextEditor.vue';
import AttachmentsTab from './orderItemDialog/AttachmentsTab.vue';
import Product from './orderItemDialog/Product.vue';
import ExamplesManager from './orderItemDialog/ExamplesManager.vue';

export default {
  name: 'OrderItemDialog',

  components: {
    VTextEditor,
    AttachmentsTab,
    Product,
    ExamplesManager,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
    clientId: {
      type: String,
      required: true,
    },
    orderItemId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      data: null,
      currentTab: 'specs',
    };
  },

  computed: {
    orderItem() {
      return this.$store.state.entities.orderItems.byId[this.orderItemId];
    },
    order() {
      return this.$store.state.entities.orders.byId[this.orderItem.orderUuid];
    },
    hasAssignee() {
      return typeof this?.order?.assigneeUuid === 'string';
    },
    hasContactPoint() {
      return typeof this?.order?.contactPointUserId === 'string';
    },
    orderAssignee() {
      if (!this?.order?.assigneeUuid) return null;
      return this.$store.state.entities.users.byId[this.order.assigneeUuid] || null;
    },
    orderContactPoint() {
      if (!this?.order?.contactPointUserId) return null;
      return this.$store.state.entities.users.byId[this.order.contactPointUserId] || null;
    },
    reviewOptions() {
      return [
        { value: null, label: 'None' },
        ...(this.hasAssignee
          ? [{ value: 'seller', label: this.orderAssignee?.fullName || 'Seller' }]
          : []),
        ...(this.hasContactPoint
          ? [{ value: 'client', label: this.orderContactPoint?.fullName || 'Client' }]
          : []),
        ...(this.hasAssignee && this.hasContactPoint ? [{ value: 'both', label: 'All' }] : []),
      ];
    },
    product() {
      if (!this?.orderItem?.orderableUuid) {
        return null;
      }
      return this.$store.state.entities.products.byId[this.orderItem.orderableUuid] || null;
    },
  },

  created() {
    this.prepareData(JSON.parse(JSON.stringify(this.orderItem)));
  },

  methods: {
    prepareData({ links = [], media = [], notes = [], requirements, review, status }) {
      this.data = {
        requirements,
        review,
        status,
        links: links
          .map((link) => {
            if (typeof link === 'string') {
              return this.$store.state.entities.links.byId[link];
            }

            return link;
          })
          .filter((link) => link),
        notes: notes
          .map((note) => {
            if (typeof note === 'string') {
              return this.$store.state.entities.notes.byId[note];
            }

            return note;
          })
          .filter((note) => note),
        media: media
          .map((medium) => {
            if (typeof medium === 'string') {
              return this.$store.state.entities.media.byId[medium];
            }

            return medium;
          })
          .filter((medium) => medium),
      };
    },
    updateRequirements(requirements) {
      if (this.data.requirements !== requirements) {
        this.data.requirements = requirements;
        this.updateOrderItem();
      }
    },
    updateReview() {
      this.updateOrderItem();
    },
    async updateOrderItem() {
      try {
        await this.$store.dispatch({
          type: 'ui/orderItems/update',
          id: this.orderItemId,
          body: this.data,
        });
      } catch (error) {
        // handle error
      }
    },
  },
};
</script>
